import { useState } from 'react';
import { FormatPicker } from '../convert/FormatPicker';
import { targetName } from '../session/exportDownload';
import { PRESET_NAMES, presetOptionLabel } from '../qc/presetLabels';
import { useCustomPresets } from '../qc/customPresetsContext';
import {
  isBuiltinPreset,
  presetExists,
  resolvePresetLabel,
  resolveRuleset,
  type PresetId,
} from '../qc/customPresets';
import { downloadBytes } from '../session/download';
import { friendlyErrorMessage } from '../session/session';
import { UpgradePrompt } from '../license/UpgradePrompt';
import { track } from '../analytics/client';
import { bucketCount, bucketFileCount } from '../analytics/catalog';
import { buildZip } from './zip';
import { workerRunner } from './workerRunner';
import { useBatch } from './context';
import { clearBatch } from './ingest';
import type { CustomPresetEntry } from '../qc/customPresets';
import type { BatchFileResult, BatchJob, BatchRunner } from './types';
import './BatchPanel.css';

/**
 * The main-thread resolution boundary (spec §4/§10): a built-in preset stays a
 * compact name (the worker resolves it via its own static `PRESETS`); a custom
 * preset becomes its full {@link Ruleset} object — plain JSON that structured-
 * clones across the `postMessage` boundary — because the worker cannot read
 * `localStorage`. Callers guard for `''` (no QC) before calling this.
 */
function toJobPreset(
  id: PresetId,
  customs: readonly CustomPresetEntry[],
): NonNullable<BatchJob['qcPreset']> {
  return isBuiltinPreset(id) ? id : resolveRuleset(id, customs);
}

/**
 * The batch workspace (spec §4.2.8, Pro), extracted from the Batch route so it
 * mounts inside Convert too (cue-forge-9px): batch is the "many files" branch of
 * one Convert drop zone, not a separate mode. Given a queue (in the BatchProvider,
 * so it survives mode switches), choose one operation — convert (+ optional shift
 * / QC / auto-fix) — apply it to all in a Web Worker so the UI never freezes, then
 * download every result as a single ZIP. Pro-gated inline (spec §7.5): a free user
 * sees the queued-file list preview plus an upgrade prompt, never a hidden tab.
 * The `runner` is injectable so tests drive the real engine in-process.
 */
export function BatchPanel({
  isPro,
  runner = workerRunner,
}: {
  isPro: boolean;
  runner?: BatchRunner;
}) {
  const batch = useBatch();
  const {
    files,
    target,
    setTarget,
    shift,
    setShift,
    preset,
    setPreset,
    autoFix,
    setAutoFix,
    notices,
    running,
    setRunning,
    progress,
    setProgress,
    results,
    setResults,
    runJob,
    setRunJob,
    runError,
    setRunError,
  } = batch;
  const { presets: customs } = useCustomPresets();
  const [presetNotice, setPresetNotice] = useState('');
  // The underlying message behind a collapsed run failure. The worker runner
  // rehydrates crashes as plain `Error(message)` (workerRunner.ts), so the
  // friendlyErrorMessage allowlist can never match — without this line the real
  // reason was lost entirely. Shown as a dim secondary "Details:" line, keeping
  // raw strings out of the primary copy (w28.12) while preserving diagnostics.
  const [runErrorDetail, setRunErrorDetail] = useState<string | null>(null);

  async function onRun() {
    if (files.length === 0 || running) return;
    // Explicit-run guard (spec §6): a selected custom preset that no longer resolves
    // (deleted here or in another tab) must block and be surfaced, never silently
    // run without QC in a compliance product. `''` (no QC) is a valid selection.
    if (preset !== '' && !presetExists(preset, customs)) {
      setPreset('');
      setPresetNotice('The selected preset no longer exists — QC was cleared. Re-run to continue.');
      return;
    }
    setPresetNotice('');
    const shiftMs = shift.trim() === '' ? undefined : Number(shift);
    // Snapshot the job settings: the results table renders from this snapshot,
    // so editing the form after a run never rewrites history. A custom preset is
    // resolved to its Ruleset object here on the main thread (spec §4/§10).
    const job: BatchJob = {
      target,
      shiftMs: Number.isFinite(shiftMs) ? shiftMs : undefined,
      qcPreset: preset === '' ? undefined : toJobPreset(preset, customs),
      autoFix: preset === '' ? false : autoFix,
      // License is resolved here on the main thread (the worker can't read it);
      // Pro-format inputs parse only in a licensed run.
      allowPro: isPro,
    };
    setRunning(true);
    setResults([]);
    setRunJob(job);
    setRunError(null);
    setRunErrorDetail(null);
    setProgress({ done: 0, total: files.length });
    const collected: BatchFileResult[] = [];
    try {
      const final = await runner(files, job, (done, total, result) => {
        collected.push(result);
        setResults([...collected]);
        setProgress({ done, total });
      });
      setResults(final);
      track('batch_run', {
        files: bucketFileCount(files.length),
        failures: bucketCount(final.filter((r) => r.status === 'error').length),
      });
    } catch (err) {
      // Keep whatever partial results the progress callback already delivered.
      setResults([...collected]);
      // A crashed run is still a completed attempt — count it with the failures
      // observed so far (the partial results the progress callback delivered).
      track('batch_run', {
        files: bucketFileCount(files.length),
        failures: bucketCount(collected.filter((r) => r.status === 'error').length),
      });
      // A worker crash surfaces as an internal Error whose message was never
      // written for users — collapse it to calm copy with a path forward
      // (cue-forge-w28.12). Per-file failures are reported in the table instead.
      const friendly = friendlyErrorMessage(
        err,
        'The run stopped unexpectedly. The results below are what completed — try running the batch again.',
      );
      setRunError(friendly);
      setRunErrorDetail(
        err instanceof Error && err.message.trim() !== '' && err.message !== friendly
          ? err.message
          : null,
      );
    } finally {
      setRunning(false);
    }
  }

  async function onDownloadZip() {
    const zip = await buildZip(results);
    downloadBytes('cueforge-batch.zip', zip, 'application/zip');
  }

  const okCount = results.filter((r) => r.status === 'ok').length;
  const done = !running && results.length > 0;

  return (
    <div className="cf-batch">
      {notices.length > 0 && (
        <ul className="cf-batch__notices" role="alert">
          {notices.map((n, i) => (
            <li key={i} className="cf-batch__warn">
              {n}
            </li>
          ))}
        </ul>
      )}

      <p className="cf-batch__count">
        {files.length} {files.length === 1 ? 'file' : 'files'} queued
        {files.length > 0 && !running && (
          <button
            type="button"
            className="cf-btn cf-btn--sm cf-batch__clear"
            onClick={() => clearBatch(batch)}
          >
            Clear queue
          </button>
        )}
      </p>
      {files.length > 0 && (
        <ul className="cf-batch__files">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="cf-mono">
              {f.name}
            </li>
          ))}
        </ul>
      )}

      {!isPro ? (
        <UpgradePrompt feature="Batch processing" surface="batch" />
      ) : (
        <>
          <div className="cf-batch__job">
            <FormatPicker
              label="Convert to"
              ariaLabel="Batch target format"
              value={target}
              onChange={setTarget}
              isPro={isPro}
            />

            <label className="cf-field">
              <span>Shift (ms)</span>
              <input
                aria-label="Shift milliseconds"
                className="cf-input"
                inputMode="numeric"
                value={shift}
                placeholder="0"
                onChange={(e) => setShift(e.target.value)}
              />
            </label>

            <label className="cf-field">
              <span>QC preset</span>
              <select
                aria-label="Batch QC preset"
                className="cf-select"
                value={preset}
                onChange={(e) => setPreset(e.target.value)}
              >
                <option value="">None</option>
                {PRESET_NAMES.map((p) => (
                  <option key={p} value={p}>
                    {presetOptionLabel(p)}
                  </option>
                ))}
                {customs.length > 0 && (
                  <optgroup label="Custom">
                    {customs.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {resolvePresetLabel(entry.id, customs)}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </label>

            <label className="cf-batch__check">
              <input
                type="checkbox"
                checked={autoFix}
                disabled={preset === ''}
                onChange={(e) => setAutoFix(e.target.checked)}
              />
              <span>Auto-fix</span>
            </label>
          </div>

          <div className="cf-batch__actions">
            <button
              type="button"
              className="cf-btn cf-btn--primary"
              onClick={() => void onRun()}
              disabled={files.length === 0 || running}
            >
              {running ? 'Processing…' : 'Run batch'}
            </button>
            {progress && (
              <span className="cf-batch__progress" aria-live="polite">
                {progress.done} / {progress.total}
              </span>
            )}
            {done && okCount > 0 && (
              <button type="button" className="cf-btn" onClick={() => void onDownloadZip()}>
                Download ZIP ({okCount})
              </button>
            )}
          </div>

          {/* Always-mounted polite live region: announces run completion to AT. It
              must exist before the message is set or it is never announced. */}
          <p className="cf-visually-hidden" role="status" aria-live="polite">
            {done
              ? `Batch complete: ${okCount} of ${progress?.total ?? results.length} succeeded`
              : ''}
          </p>

          {presetNotice && (
            <p className="cf-batch__warn" role="alert">
              {presetNotice}
            </p>
          )}

          {runError && (
            <>
              <p className="cf-batch__warn" role="alert">
                Batch failed: {runError}
              </p>
              {runErrorDetail && (
                <p className="cf-batch__error-detail">Details: {runErrorDetail}</p>
              )}
            </>
          )}

          {results.length > 0 && (
            <table className="cf-batch__table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Status</th>
                  <th>Output</th>
                  <th>Cues</th>
                  <th>Issues</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr
                    key={`${r.name}-${i}`}
                    className={r.status === 'error' ? 'cf-batch__row--error' : undefined}
                  >
                    <td className="cf-mono">{r.name}</td>
                    <td>{r.status === 'ok' ? 'OK' : 'Error'}</td>
                    <td className="cf-mono">{r.outputName ?? '—'}</td>
                    <td>{r.cueCount ?? '—'}</td>
                    <td>
                      {r.issueCount === undefined
                        ? '—'
                        : r.fixedCount !== undefined
                          ? `${r.issueCount} (fixed ${r.fixedCount})`
                          : r.issueCount}
                    </td>
                    {/* Error, else a non-fatal warning (e.g. 0-cue empty output),
                        else the conversion the run snapshot performed. */}
                    <td>
                      {r.error ?? r.warning ?? `→ ${targetName((runJob ?? { target }).target)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
